/* eslint-disable sort-imports */
import { ComboBox } from 'src/components/ui/combobox';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import {
    BulletinAdminTabs,
    VIEW_BULLETIN_PATH,
} from 'src/components/bulletins-form/tabs';
import {
    BulletinDateField as DateField,
    BulletinTextField as Field,
} from 'src/components/bulletins-form';
import { RadioButton } from 'src/components/ui/radioButton';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import Spinner from 'src/components/ui/spinner';
import React, {
    ChangeEvent,
    FormEvent,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { BulletinsService } from 'src/services/bulletins.service';
import {
    BULLETIN_COUNTRY_NONE,
    BULLETIN_LANGUAGE_CODES as LANGUAGE_CODES,
    BULLETIN_LANGUAGES as LANGUAGES,
    BULLETIN_TYPE_CODES,
    BULLETIN_TYPES,
    getBulletinCountryCode,
} from 'src/utils/bulletins';
import {
    BULLETIN_DOCUMENT_TYPE,
    MAX_DOCUMENT_BYTES,
    uploadBulletinDocument,
} from 'src/utils/bulletin-document-upload';

import './styles.scss';

const PREVIOUS_HISTORY_ENTRY = -1;
const EMPTY_ERRORS = 0;
const BULLETIN_NUMBER_MAX_LENGTH = 60;
const BULLETIN_TITLE_MAX_LENGTH = 100;
const UPLOAD_BULLETIN_PATH = '/dashboard/systemadmin/upload-bulletin';
const SELECT_DISTRIBUTION_RULE_PATH =
    '/dashboard/systemadmin/view-distribution-rule';

const formatDateForBulletinApi = (date: string): string => {
    const [year, month, day] = date.split('-');
    return year && month && day ? `${month}/${day}/${year}` : date;
};

interface IUploadBulletinForm {
    file: File | null;
    bulletinNumber: string;
    bulletinTitle: string;
    type: string;
    language: string;
    startDate: string;
    endDate: string;
    distributionRule: string;
    distributionId: string;
    country: string;
}

const INITIAL_FORM: IUploadBulletinForm = {
    file: null,
    bulletinNumber: '',
    bulletinTitle: '',
    type: '',
    language: LANGUAGES[0],
    startDate: '',
    endDate: '',
    distributionRule: '',
    distributionId: '',
    country: BULLETIN_COUNTRY_NONE,
};

interface IUploadBulletinNavigationState {
    selectedRule?: {
        distributionId: string;
        ruleName: string;
        country?: string;
    };
    formState?: IUploadBulletinForm;
}

type UploadBulletinField =
    | 'file'
    | 'bulletinNumber'
    | 'bulletinTitle'
    | 'type'
    | 'startDate'
    | 'distributionRule';
type UploadBulletinErrors = Partial<Record<UploadBulletinField, string>>;

const UploadBulletin: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [form, setForm] = useState<IUploadBulletinForm>(INITIAL_FORM);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [submitError, setSubmitError] = useState(false);
    const [isSubmitMessageVisible, setIsSubmitMessageVisible] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<UploadBulletinErrors>({});
    // Kept between retries so a failed save does not leave several uploaded PDFs behind.
    const documentIdRef = useRef<string | null>(null);

    useEffect(() => {
        const state = location.state as IUploadBulletinNavigationState | null;

        if (!state?.formState && !state?.selectedRule) return;

        setForm((currentForm) => ({
            ...currentForm,
            ...state.formState,
            ...(state.selectedRule && {
                distributionRule: state.selectedRule.ruleName,
                distributionId: state.selectedRule.distributionId,
                // The rule decides the country; a rule without country is 0.
                country: getBulletinCountryCode(state.selectedRule.country),
            }),
        }));
        navigate(location.pathname, { replace: true, state: null });
    }, []);

    const updateForm = (
        field: keyof IUploadBulletinForm,
        value: string | File | null,
    ) => {
        setForm((currentForm) => ({ ...currentForm, [field]: value }));
        setFieldErrors((currentErrors) => {
            const nextErrors = { ...currentErrors };
            delete nextErrors[field as UploadBulletinField];
            return nextErrors;
        });
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateForm('file', event.target.files?.[0] ?? null);
    };

    const isFormValid = Boolean(
        form.file &&
            form.bulletinNumber.trim() &&
            form.bulletinTitle.trim() &&
            form.type &&
            form.startDate &&
            form.distributionRule,
    );

    const validateForm = (): UploadBulletinErrors => {
        const requiredMessage = t('Label.Field.Error.Required', {
            defaultValue: 'Field required',
        });
        const errors: UploadBulletinErrors = {};

        if (!form.file) errors.file = requiredMessage;
        else if (form.file.type !== 'application/pdf') {
            errors.file = text('FileError', 'Please attach a valid PDF file.');
        } else if (form.file.size > MAX_DOCUMENT_BYTES) {
            errors.file = text(
                'FileSizeError',
                'File size not allowed. Maximum allowed 10MB.',
            );
        }
        if (!form.bulletinNumber.trim())
            errors.bulletinNumber = requiredMessage;
        if (!form.bulletinTitle.trim()) errors.bulletinTitle = requiredMessage;
        if (!form.type) errors.type = requiredMessage;
        if (!form.startDate) errors.startDate = requiredMessage;
        if (!form.distributionRule) errors.distributionRule = requiredMessage;

        return errors;
    };

    const handleSelectRule = () => {
        navigate(SELECT_DISTRIBUTION_RULE_PATH, {
            state: {
                selectMode: true,
                returnTo: UPLOAD_BULLETIN_PATH,
                formState: form,
            },
        });
    };

    const handleReset = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.currentTarget.form?.reset();
        documentIdRef.current = null;
        setForm(INITIAL_FORM);
        setFieldErrors({});
        setSubmitMessage('');
        setSubmitError(false);
        setIsSubmitMessageVisible(false);
    };
    const handleCancel = () => {
        navigate(PREVIOUS_HISTORY_ENTRY);
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const errors = validateForm();
        setFieldErrors(errors);
        if (Object.keys(errors).length > EMPTY_ERRORS) {
            setSubmitError(true);
            setSubmitMessage(
                text(
                    'ValidationError',
                    'Please correct the highlighted fields before submitting.',
                ),
            );
            setIsSubmitMessageVisible(true);
            return;
        }
        if (!form.file || !isFormValid || isSubmitting) return;

        setIsSubmitting(true);
        setSubmitMessage('');
        setSubmitError(false);
        setIsSubmitMessageVisible(false);
        let isUploaded = false;

        try {
            const documentId = documentIdRef.current ?? uuidv4();
            documentIdRef.current = documentId;
            const service = new BulletinsService();

            await uploadBulletinDocument(service, form.file, documentId);
            await service.upsertBulletin({
                documentId,
                documentType: BULLETIN_DOCUMENT_TYPE,
                bulletinType: BULLETIN_TYPE_CODES[form.type],
                bulletinNumber: form.bulletinNumber.trim(),
                distributionId: form.distributionId,
                title: form.bulletinTitle.trim(),
                validFromDate: formatDateForBulletinApi(form.startDate),
                validThroughDate: form.endDate
                    ? formatDateForBulletinApi(form.endDate)
                    : null,
                language: LANGUAGE_CODES[form.language],
                country: form.country,
                visualStyles: '{}',
            });
            documentIdRef.current = null;
            isUploaded = true;
        } catch {
            setSubmitError(true);
            setSubmitMessage(text('SubmitError', 'Unable to upload bulletin.'));
            setIsSubmitMessageVisible(true);
        } finally {
            setIsSubmitting(false);
        }

        if (isUploaded) {
            navigate(VIEW_BULLETIN_PATH, {
                state: {
                    successMessage: text(
                        'SubmitSuccess',
                        'Bulletin have been successfully uploaded',
                    ),
                },
            });
        }
    };

    const text = (key: string, defaultValue: string) =>
        t(`SystemAdmin.UploadBulletinPage.${key}`, { defaultValue });

    return (
        <>
            <main className='upload-bulletin'>
                {submitMessage && (
                    <Notification
                        content={submitMessage}
                        type={
                            submitError
                                ? NotificationType.ERROR
                                : NotificationType.SUCCESS
                        }
                        visibility={isSubmitMessageVisible}
                        setVisibility={setIsSubmitMessageVisible}
                    />
                )}

                <div className='page-title'>
                    <h1>{text('PageTitle', 'Online Bulletins')}</h1>
                </div>

                <BulletinAdminTabs active='upload' />

                <section className='c-form-section upload-bulletin__card'>
                    <div className='c-form-section__content'>
                        <h2>{text('Title', 'Upload Bulletin')}</h2>
                        <p className='upload-bulletin__description'>
                            {text(
                                'Description',
                                'Enter information about the bulletin in the fields below.',
                            )}
                        </p>

                        <form onSubmit={handleSubmit}>
                            <div className='upload-bulletin__fields'>
                                <div className='c-form-field upload-bulletin__file-field'>
                                    <label
                                        className='c-form-field__label'
                                        htmlFor='bulletin-file'
                                    >
                                        {text(
                                            'FileLabel',
                                            'Attach bulletin (PDF)',
                                        )}
                                        *
                                    </label>
                                    <div
                                        className={`c-upload ${
                                            fieldErrors.file ? 'is-error' : ''
                                        }`}
                                    >
                                        <input
                                            id='bulletin-file'
                                            className={`c-upload__input ${
                                                fieldErrors.file
                                                    ? 'is-error'
                                                    : ''
                                            }`}
                                            type='file'
                                            accept='application/pdf'
                                            onChange={handleFileChange}
                                        />
                                        {fieldErrors.file && (
                                            <span className='c-error-message'>
                                                {fieldErrors.file}
                                            </span>
                                        )}
                                        <div className='c-upload__description'>
                                            <span className='c-upload__description-supplementary-text'>
                                                {form.file?.name ??
                                                    text(
                                                        'NoFile',
                                                        'No file chosen.',
                                                    )}
                                            </span>
                                        </div>
                                        <div
                                            className='c-upload__button c-btn'
                                            aria-hidden='true'
                                        >
                                            <span>
                                                {text(
                                                    'ChooseFile',
                                                    'Choose File',
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className='upload-bulletin__row'>
                                    <Field
                                        id='bulletin-number'
                                        label={text(
                                            'NumberLabel',
                                            'Bulletin Number',
                                        )}
                                        placeholder={text(
                                            'NumberPlaceholder',
                                            'Alphanumeric with hyphens (e.g., BL-2026-09)',
                                        )}
                                        maxLength={BULLETIN_NUMBER_MAX_LENGTH}
                                        value={form.bulletinNumber}
                                        onChange={(value) =>
                                            updateForm('bulletinNumber', value)
                                        }
                                        error={fieldErrors.bulletinNumber}
                                    />
                                    <Field
                                        id='bulletin-title'
                                        label={text(
                                            'TitleLabel',
                                            'Bulletin Title',
                                        )}
                                        placeholder={text(
                                            'TitlePlaceholder',
                                            'Enter complete bulletin title',
                                        )}
                                        maxLength={BULLETIN_TITLE_MAX_LENGTH}
                                        value={form.bulletinTitle}
                                        onChange={(value) =>
                                            updateForm('bulletinTitle', value)
                                        }
                                        error={fieldErrors.bulletinTitle}
                                    />
                                </div>

                                <div className='upload-bulletin__row'>
                                    <div className='c-form-field'>
                                        <label
                                            className='c-form-field__label'
                                            htmlFor='bulletin-type'
                                        >
                                            {text('TypeLabel', 'Type')}*
                                        </label>
                                        <ComboBox
                                            name='bulletin-type'
                                            options={BULLETIN_TYPES}
                                            placeholder={text(
                                                'TypePlaceholder',
                                                'Select type',
                                            )}
                                            selectedOption={form.type}
                                            onSelect={(value: string) =>
                                                updateForm('type', value)
                                            }
                                            hasError={Boolean(fieldErrors.type)}
                                        />
                                        {fieldErrors.type && (
                                            <span className='c-error-message'>
                                                {fieldErrors.type}
                                            </span>
                                        )}
                                    </div>
                                    <div className='c-form-field'>
                                        <label className='c-form-field__label'>
                                            {text('LanguageLabel', 'Language')}
                                        </label>
                                        <RadioButton
                                            name='bulletin-language'
                                            options={LANGUAGES}
                                            labels={[
                                                text('English', 'English'),
                                                text('French', 'French'),
                                            ]}
                                            selectedOption={form.language}
                                            onSelect={(value: string) =>
                                                updateForm('language', value)
                                            }
                                        />
                                    </div>
                                </div>

                                <div className='upload-bulletin__row'>
                                    <DateField
                                        label={`${text(
                                            'StartDateLabel',
                                            'Start Date',
                                        )}*`}
                                        placeholder={text(
                                            'StartDatePlaceholder',
                                            'Select start date',
                                        )}
                                        value={form.startDate}
                                        onChange={(value) =>
                                            updateForm('startDate', value)
                                        }
                                        error={fieldErrors.startDate}
                                    />
                                    <DateField
                                        label={text('EndDateLabel', 'End Date')}
                                        placeholder={text(
                                            'EndDatePlaceholder',
                                            'Select end date',
                                        )}
                                        value={form.endDate}
                                        onChange={(value) =>
                                            updateForm('endDate', value)
                                        }
                                    />
                                </div>

                                <div className='upload-bulletin__distribution'>
                                    <div className='c-form-field'>
                                        <label
                                            className='c-form-field__label'
                                            htmlFor='bulletin-distribution-rule'
                                        >
                                            {text(
                                                'DistributionLabel',
                                                'Choose Distribution Rule',
                                            )}
                                            *
                                        </label>
                                        <div className='upload-bulletin__distribution-control'>
                                            <div className='c-input upload-bulletin__distribution-display'>
                                                <input
                                                    id='bulletin-distribution-rule'
                                                    className={`c-input__input ${
                                                        fieldErrors.distributionRule
                                                            ? 'is-error'
                                                            : ''
                                                    }`}
                                                    type='text'
                                                    readOnly
                                                    placeholder={text(
                                                        'DistributionPlaceholder',
                                                        'No distribution rule selected',
                                                    )}
                                                    value={
                                                        form.distributionRule
                                                    }
                                                />
                                            </div>
                                            <button
                                                className='c-btn c-btn--secondary'
                                                type='button'
                                                onClick={handleSelectRule}
                                            >
                                                <span className='c-btn__text'>
                                                    {text(
                                                        'SelectRule',
                                                        'Select Rule',
                                                    )}
                                                </span>
                                            </button>
                                            {fieldErrors.distributionRule && (
                                                <span className='c-error-message'>
                                                    {
                                                        fieldErrors.distributionRule
                                                    }
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <p className='upload-bulletin__helper'>
                                {text(
                                    'DistributionHelp',
                                    'Click "Select Rule" to define the bulletin\'s audience in the Dealer Extranet. After choosing a distribution rule, you will be redirected back to this screen.',
                                )}
                            </p>

                            <div className='upload-bulletin__actions'>
                                <button
                                    className='c-btn c-btn--secondary'
                                    type='button'
                                    onClick={handleCancel}
                                >
                                    <span className='c-btn__text'>
                                        {text('Cancel', 'Cancel')}
                                    </span>
                                </button>
                                <button
                                    className='c-btn c-btn--secondary'
                                    type='button'
                                    onClick={handleReset}
                                >
                                    <span className='c-btn__text'>
                                        {text('Reset', 'Reset')}
                                    </span>
                                </button>
                                <button
                                    className='c-btn'
                                    type='submit'
                                    disabled={!isFormValid || isSubmitting}
                                >
                                    <span className='c-btn__text'>
                                        {isSubmitting ? (
                                            <>
                                                <Spinner />
                                                {text(
                                                    'Submitting',
                                                    'Uploading...',
                                                )}
                                            </>
                                        ) : (
                                            text('Submit', 'Upload Bulletin')
                                        )}
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </section>
            </main>
            <FooterPrivacy />
        </>
    );
};

export default UploadBulletin;
